/**
 * ICU MessageFormat — the subset a messenger actually needs.
 *
 * Why a parser rather than string interpolation: languages do not agree on how
 * many plural forms exist. English has two, Hebrew has three, Arabic has six,
 * Japanese has one. `` `${n} messages` `` is only correct in English, and
 * `n === 1 ? a : b` is only correct in languages with exactly two forms. The
 * plural category has to come from `Intl.PluralRules` for the target locale,
 * which means the *translation* has to be able to name its own categories.
 *
 * The same argument applies to word order. A translator must be able to move
 * `{name}` to the front of a sentence; concatenating fragments in code makes
 * that impossible, which is why the brief forbids it outright.
 *
 * Supported:
 *
 *   {name}                                  argument
 *   {n, number}  {n, number, percent}       Intl.NumberFormat
 *   {t, date, short|medium|long}            Intl.DateTimeFormat
 *   {t, time, short|medium}                 Intl.DateTimeFormat
 *   {n, plural, one {…} other {…}}          Intl.PluralRules, # substitutes n
 *   {n, plural, =0 {…} one {…} other {…}}   exact-value match wins over category
 *   {n, selectordinal, one {…} other {…}}   ordinal plural rules
 *   {g, select, female {…} other {…}}       exact match on a string argument
 *
 * Branches nest, so a plural branch may contain another argument or plural.
 * Quoting follows ICU: '{' is a literal brace, '' is a literal apostrophe.
 */
import { isolate } from '@vav/protocol';

export type MessageValues = Record<string, string | number | Date | undefined>;

type Node =
  | { kind: 'text'; value: string }
  | { kind: 'arg'; name: string }
  | { kind: 'number'; name: string; style?: string }
  | { kind: 'date'; name: string; style: string; timeOnly: boolean }
  | { kind: 'plural'; name: string; ordinal: boolean; branches: Map<string, Node[]> }
  | { kind: 'select'; name: string; branches: Map<string, Node[]> };

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

class Parser {
  #src: string;
  #pos = 0;

  constructor(src: string) {
    this.#src = src;
  }

  parse(depth = 0): Node[] {
    const nodes: Node[] = [];
    let text = '';

    const flush = () => {
      if (text) {
        nodes.push({ kind: 'text', value: text });
        text = '';
      }
    };

    while (this.#pos < this.#src.length) {
      const char = this.#src[this.#pos];

      // A closing brace ends a branch, but at the top level it is just text --
      // otherwise a message containing a stray '}' would throw at runtime.
      if (char === '}' && depth > 0) break;

      if (char === "'") {
        const next = this.#src[this.#pos + 1];
        if (next === "'") {
          text += "'";
          this.#pos += 2;
          continue;
        }
        // A quote only escapes when it precedes a syntax character; elsewhere
        // it is an ordinary apostrophe, which matters in "l'utilisateur".
        if (next === '{' || next === '}' || next === '#') {
          this.#pos += 1;
          const end = this.#src.indexOf("'", this.#pos);
          if (end === -1) {
            text += this.#src.slice(this.#pos);
            this.#pos = this.#src.length;
          } else {
            text += this.#src.slice(this.#pos, end);
            this.#pos = end + 1;
          }
          continue;
        }
        text += "'";
        this.#pos += 1;
        continue;
      }

      if (char === '{') {
        flush();
        nodes.push(this.#parseArgument());
        continue;
      }

      text += char;
      this.#pos += 1;
    }

    flush();
    return nodes;
  }

  #parseArgument(): Node {
    this.#pos += 1; // '{'
    this.#skipSpace();
    const name = this.#readUntil(/[,}]/);
    this.#skipSpace();

    if (this.#src[this.#pos] === '}') {
      this.#pos += 1;
      return { kind: 'arg', name };
    }

    this.#pos += 1; // ','
    this.#skipSpace();
    const type = this.#readUntil(/[,}]/);
    this.#skipSpace();

    if (type === 'number') {
      let style: string | undefined;
      if (this.#src[this.#pos] === ',') {
        this.#pos += 1;
        this.#skipSpace();
        style = this.#readUntil(/}/);
      }
      this.#expect('}');
      return { kind: 'number', name, style };
    }

    if (type === 'date' || type === 'time') {
      let style = 'short';
      if (this.#src[this.#pos] === ',') {
        this.#pos += 1;
        this.#skipSpace();
        style = this.#readUntil(/}/) || 'short';
      }
      this.#expect('}');
      return { kind: 'date', name, style, timeOnly: type === 'time' };
    }

    if (type === 'plural' || type === 'selectordinal' || type === 'select') {
      this.#expect(',');
      const branches = this.#parseBranches();
      this.#expect('}');
      return type === 'select'
        ? { kind: 'select', name, branches }
        : { kind: 'plural', name, ordinal: type === 'selectordinal', branches };
    }

    // Unknown type: treat as a plain argument rather than throwing. A bad
    // translation should degrade to showing the value, not blank the screen.
    this.#readUntil(/}/);
    this.#expect('}');
    return { kind: 'arg', name };
  }

  #parseBranches(): Map<string, Node[]> {
    const branches = new Map<string, Node[]>();
    for (;;) {
      this.#skipSpace();
      if (this.#pos >= this.#src.length || this.#src[this.#pos] === '}') break;

      const key = this.#readUntil(/[{\s]/).trim();
      this.#skipSpace();
      if (this.#src[this.#pos] !== '{') break;

      this.#pos += 1;
      branches.set(key, this.parse(1));
      this.#expect('}');
    }
    return branches;
  }

  #skipSpace(): void {
    while (this.#pos < this.#src.length && /\s/.test(this.#src[this.#pos])) this.#pos += 1;
  }

  #readUntil(stop: RegExp): string {
    const start = this.#pos;
    while (this.#pos < this.#src.length && !stop.test(this.#src[this.#pos])) this.#pos += 1;
    return this.#src.slice(start, this.#pos).trim();
  }

  #expect(char: string): void {
    if (this.#src[this.#pos] === char) this.#pos += 1;
  }
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

const astCache = new Map<string, Node[]>();
const pluralCache = new Map<string, Intl.PluralRules>();
const numberCache = new Map<string, Intl.NumberFormat>();
const dateCache = new Map<string, Intl.DateTimeFormat>();

function pluralRules(locale: string, ordinal: boolean): Intl.PluralRules {
  const key = `${locale}:${ordinal}`;
  let rules = pluralCache.get(key);
  if (!rules) {
    rules = new Intl.PluralRules(locale, { type: ordinal ? 'ordinal' : 'cardinal' });
    pluralCache.set(key, rules);
  }
  return rules;
}

const DATE_STYLES: Record<string, Intl.DateTimeFormatOptions> = {
  short: { day: 'numeric', month: 'numeric', year: 'numeric' },
  medium: { day: 'numeric', month: 'short', year: 'numeric' },
  long: { day: 'numeric', month: 'long', year: 'numeric' },
};

const TIME_STYLES: Record<string, Intl.DateTimeFormatOptions> = {
  short: { hour: 'numeric', minute: '2-digit' },
  medium: { hour: 'numeric', minute: '2-digit', second: '2-digit' },
};

/**
 * Render a message.
 *
 * String arguments are wrapped in Unicode isolates. A display name, filename or
 * phone number whose direction differs from the sentence around it will
 * otherwise drag neighbouring punctuation to the wrong end -- the most common
 * bidi bug, and invisible to anyone testing in one language. Numbers and dates
 * are already formatted for the locale and need no isolation.
 */
export function formatMessage(locale: string, pattern: string, values: MessageValues = {}): string {
  let ast = astCache.get(pattern);
  if (!ast) {
    ast = new Parser(pattern).parse();
    astCache.set(pattern, ast);
  }
  return render(ast, locale, values);
}

function render(nodes: Node[], locale: string, values: MessageValues, pluralValue?: number): string {
  let out = '';

  for (const node of nodes) {
    switch (node.kind) {
      case 'text':
        out += pluralValue === undefined ? node.value : node.value.split('#').join(String(pluralValue));
        break;

      case 'arg': {
        const value = values[node.name];
        if (value === undefined) break;
        out += typeof value === 'string' ? isolate(value) : String(value);
        break;
      }

      case 'number': {
        const value = Number(values[node.name] ?? 0);
        const key = `${locale}:${node.style ?? ''}`;
        let formatter = numberCache.get(key);
        if (!formatter) {
          formatter = new Intl.NumberFormat(
            locale,
            node.style === 'percent' ? { style: 'percent' } : undefined,
          );
          numberCache.set(key, formatter);
        }
        out += formatter.format(value);
        break;
      }

      case 'date': {
        const raw = values[node.name];
        const when = raw instanceof Date ? raw : new Date(Number(raw ?? 0));
        const key = `${locale}:${node.timeOnly ? 't' : 'd'}:${node.style}`;
        let formatter = dateCache.get(key);
        if (!formatter) {
          const options = node.timeOnly
            ? (TIME_STYLES[node.style] ?? TIME_STYLES.short)
            : (DATE_STYLES[node.style] ?? DATE_STYLES.short);
          formatter = new Intl.DateTimeFormat(locale, options);
          dateCache.set(key, formatter);
        }
        out += formatter.format(when);
        break;
      }

      case 'plural': {
        const value = Number(values[node.name] ?? 0);
        // An exact `=N` branch outranks the category, so "no messages" can be
        // written naturally rather than forced through the `zero` category,
        // which most languages do not have.
        const exact = node.branches.get(`=${value}`);
        const category = pluralRules(locale, node.ordinal).select(value);
        const branch = exact ?? node.branches.get(category) ?? node.branches.get('other') ?? [];
        out += render(branch, locale, values, value);
        break;
      }

      case 'select': {
        const value = String(values[node.name] ?? '');
        const branch = node.branches.get(value) ?? node.branches.get('other') ?? [];
        out += render(branch, locale, values, pluralValue);
        break;
      }
    }
  }

  return out;
}
